import type { UserModel } from "../types.js";
import { ROLE_NAME, userHasRole } from "../roles/role-access.js";

/**
 * License capability keys used for client-side route guards and API alignment.
 */
export type LicenseAuthorization =
  | "backup"
  | "live"
  | "planning"
  | "layoutEditor"
  | "customDisplay"
  | "remoteCustomDisplay"
  | "controlRoom";

/** Boolean columns on {@link License} / {@link LicensedUserLicense}. */
export type LicenseAuthorizationField =
  | "hasBackupAccess"
  | "hasLiveAccess"
  | "hasPlanningAccess"
  | "hasLayoutEditorAccess"
  | "hasCustomDisplayAccess"
  | "hasRemoteCustomDisplayAccess"
  | "hasControlRoomAccess";

export type LicenseAuthorizationCheckMode = "any" | "all";

export type LicenseAuthorizationSnapshot = Partial<
  Record<LicenseAuthorizationField, boolean>
>;

export const LICENSE_AUTHORIZATION_FIELDS: Record<
  LicenseAuthorization,
  LicenseAuthorizationField
> = {
  backup: "hasBackupAccess",
  live: "hasLiveAccess",
  planning: "hasPlanningAccess",
  layoutEditor: "hasLayoutEditorAccess",
  customDisplay: "hasCustomDisplayAccess",
  remoteCustomDisplay: "hasRemoteCustomDisplayAccess",
  controlRoom: "hasControlRoomAccess",
};

/**
 * Normalizes hook/controller input into a non-empty authorization list.
 */
export const normalizeLicenseAuthorizations = (
  required: LicenseAuthorization | LicenseAuthorization[],
): LicenseAuthorization[] => {
  const list = Array.isArray(required) ? required : [required];
  if (list.length === 0) {
    throw new Error("At least one license authorization is required");
  }
  return list;
};

/**
 * Returns whether a license snapshot satisfies the required authorizations.
 *
 * @param mode - `any` (default) matches backend OR semantics; `all` requires every flag.
 */
export const licenseHasAuthorizations = (
  license: LicenseAuthorizationSnapshot | null | undefined,
  required: LicenseAuthorization | LicenseAuthorization[],
  mode: LicenseAuthorizationCheckMode = "any",
): boolean => {
  const list = normalizeLicenseAuthorizations(required);
  if (!license) {
    return false;
  }

  const checks = list.map(
    (authorization) =>
      license[LICENSE_AUTHORIZATION_FIELDS[authorization]] === true,
  );

  return mode === "all" ? checks.every(Boolean) : checks.some(Boolean);
};

/** Returns true when the user has the Admin role (or equivalent). */
export const userIsAdmin = (user: UserModel | null | undefined): boolean =>
  userHasRole(user, ROLE_NAME.Admin);

/**
 * Checks license capabilities for UI visibility (no redirect).
 * Admins bypass capability checks when {@link allowAdminBypass} is true (default).
 */
export const userHasLicenseAuthorization = (
  user: UserModel | null | undefined,
  required: LicenseAuthorization | LicenseAuthorization[],
  options?: {
    mode?: LicenseAuthorizationCheckMode;
    allowAdminBypass?: boolean;
  },
): boolean => {
  const { mode = "any", allowAdminBypass = true } = options ?? {};
  if (!user) {
    return false;
  }
  if (allowAdminBypass && userIsAdmin(user)) {
    return true;
  }
  return licenseHasAuthorizations(user.license, required, mode);
};
